
import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // Scroll handling – adds a small shadow when the page is scrolled
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ---------------------------------------------------------------------------
  // Helper – scrolls smoothly to the contact form and closes the mobile menu
  // ---------------------------------------------------------------------------
  const scrollToForm = () => {
    setMobileMenuOpen(false);
    document
      .getElementById('contact-form')
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  // ---------------------------------------------------------------------------
  // Form submit – robust error handling & graceful UI reset
  // ---------------------------------------------------------------------------
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d67ouctbd17lru29kisg',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          // URLSearchParams works with FormData, but TypeScript expects an
          // iterable of [string, string] – casting to any keeps the original
          // behaviour while silencing the warning.
          body: new URLSearchParams(formData as any).toString(),
        }
      );

      if (response.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        console.error('Form submission failed:', response.statusText);
        setFormStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus('error');
    } finally {
      // Return to the idle state after a short delay so the user can read the
      // success / error message.
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="text-xl sm:text-2xl font-bold text-black">
              BlockFood
            </div>

            {/* Desktop CTA */}
            <button
              onClick={scrollToForm}
              className="hidden sm:block bg-[#FF007F] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#E6006F] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer text-sm"
            >
              Réserver mon rendez-vous
            </button>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="sm:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i
                className={`${
                  mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
                } text-2xl ${
                  isScrolled || mobileMenuOpen ? 'text-black' : 'text-white'
                }`}
              ></i>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden bg-white shadow-lg border-t border-gray-100 px-4 py-4">
            <button
              onClick={scrollToForm}
              className="w-full bg-[#FF007F] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#E6006F] transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer text-sm"
            >
              Réserver mon rendez-vous
            </button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/99661830527c1995f774a1300708ce30.jpg"
            alt="Restaurant moderne"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>

        <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
          <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            Augmentez vos ventes jusqu&apos;à{' '}
            <span className="text-[#FF007F]">+40%</span>… sans dépenser 1€ !
          </h1>
          <p className="text-base sm:text-xl lg:text-2xl text-white/95 mb-6 sm:mb-10 max-w-3xl mx-auto leading-relaxed px-2">
            BlockFood crée pour vous des marques virtuelles et génère des ventes
            additionnelles chaque jour sur Uber Eats, Deliveroo et plus… sans
            changer votre cuisine !
          </p>
          <button
            onClick={scrollToForm}
            className="bg-[#FF007F] text-white px-6 sm:px-10 py-3 sm:py-5 rounded-full text-base sm:text-lg font-bold hover:bg-[#E6006F] transition-all duration-300 shadow-2xl hover:shadow-[#FF007F]/50 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            J&apos;en veux savoir plus
          </button>
        </div>

        <div className="absolute bottom-6 sm:bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-3xl sm:text-4xl text-white/80"></i>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-12 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-black mb-4 sm:mb-6">
              Vous reconnaissez-vous ?
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Trop de concurrence, pas assez de commandes, marges faibles…
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {/* Card 1 */}
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gray-100">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#FF007F]/10 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-eye-off-line text-2xl sm:text-3xl text-[#FF007F]"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 sm:mb-4">
                Manque de visibilité
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                Votre restaurant se perd parmi des centaines d&apos;autres sur les
                plateformes de livraison.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gray-100">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#FF007F]/10 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-user-search-line text-2xl sm:text-3xl text-[#FF007F]"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 sm:mb-4">
                Difficulté à attirer
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                Attirer de nouveaux clients devient de plus en plus compliqué et
                coûteux.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gray-100">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#FF007F]/10 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-line-chart-line text-2xl sm:text-3xl text-[#FF007F]"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 sm:mb-4">
                Revenus stagnants
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                Malgré tous vos efforts, votre chiffre d&apos;affaires ne décolle
                pas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-12 sm:py-24 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
              C&apos;est quoi la solution{' '}
              <span className="text-[#FF007F]">BlockFood</span> ?
            </h2>
            <p className="text-base sm:text-xl text-gray-300 max-w-3xl mx-auto">
              Nous créons des marques virtuelles qui génèrent des ventes
              additionnelles pour votre restaurant
            </p>
          </div>

          {/* Avant / Après BlockFood */}
          <div className="mb-16 sm:mb-24">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
              {/* AVANT */}
              <div className="relative bg-white/5 backdrop-blur-sm rounded-3xl border-2 border-red-500/40 p-6 sm:p-10 overflow-hidden">
                <div className="absolute top-0 left-0 bg-red-500 text-white px-4 sm:px-6 py-2 rounded-br-2xl font-bold text-xs sm:text-sm tracking-wider">
                  AVANT
                </div>
                <div className="mt-8 mb-4 sm:mb-6 text-center">
                  <h3 className="text-xl sm:text-3xl font-bold text-white mb-2">
                    Le restaurant{' '}
                    <span className="text-red-400 font-extrabold">sans</span>{' '}
                    BlockFood
                  </h3>
                </div>

                {/* Desktop diagram */}
                <div className="hidden sm:flex items-center justify-between gap-3 mb-8">
                  {/* Restaurant */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/10 rounded-2xl flex items-center justify-center mb-2">
                      <i className="ri-store-2-line text-3xl sm:text-4xl text-gray-400"></i>
                    </div>
                    <span className="text-xs sm:text-sm text-gray-400 font-medium text-center">
                      Restaurant<br />
                      ABC
                    </span>
                  </div>

                  {/* Arrow + cost */}
                  <div className="flex-1 flex flex-col items-center">
                    <div className="flex items-center w-full">
                      <div className="flex-1 h-0.5 bg-gray-600"></div>
                      <i className="ri-arrow-left-right-line text-xl text-gray-500 mx-1"></i>
                      <div className="flex-1 h-0.5 bg-gray-600"></div>
                    </div>
                    <div className="flex items-center gap-1 mt-2">
                      <i className="ri-money-euro-circle-line text-lg text-red-400"></i>
                      <span className="text-xs text-red-400 font-semibold">
                        Commissions élevées
                      </span>
                    </div>
                  </div>

                  {/* Single showcase */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="flex gap-2 mb-2">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-xl flex items-center justify-center">
                        <i className="ri-riding-line text-xl sm:text-2xl text-gray-400"></i>
                      </div>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-xl flex items-center justify-center">
                        <i className="ri-e-bike-line text-xl sm:text-2xl text-gray-400"></i>
                      </div>
                    </div>
                    <span className="text-xs sm:text-sm text-gray-400 font-medium text-center">
                      1 seule<br />
                      vitrine
                    </span>
                  </div>

                  {/* Arrow */}
                  <div className="flex-1 flex flex-col items-center">
                    <div className="flex items-center w-full">
                      <div className="flex-1 h-0.5 bg-gray-600"></div>
                      <i className="ri-arrow-right-line text-xl text-gray-500 mx-1"></i>
                      <div className="flex-1 h-0.5 bg-gray-600"></div>
                    </div>
                  </div>

                  {/* Few customers */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/10 rounded-2xl flex items-center justify-center mb-2">
                      <i className="ri-group-line text-3xl sm:text-4xl text-gray-400"></i>
                    </div>
                    <span className="text-xs sm:text-sm text-gray-400 font-medium">
                      Peu de clients
                    </span>
                  </div>
                </div>

                {/* Mobile diagram */}
                <div className="flex sm:hidden items-center justify-center gap-2 mb-6">
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mb-1">
                      <i className="ri-store-2-line text-2xl text-gray-400"></i>
                    </div>
                    <span className="text-[10px] text-gray-400 font-medium text-center">
                      Restaurant
                    </span>
                  </div>
                  <i className="ri-arrow-right-line text-lg text-gray-500"></i>
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mb-1">
                      <i className="ri-riding-line text-2xl text-gray-400"></i>
                    </div>
                    <span className="text-[10px] text-gray-400 font-medium text-center">
                      1 vitrine
                    </span>
                  </div>
                  <i className="ri-arrow-right-line text-lg text-gray-500"></i>
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mb-1">
                      <i className="ri-group-line text-2xl text-gray-400"></i>
                    </div>
                    <span className="text-[10px] text-gray-400 font-medium text-center">
                      Peu de clients
                    </span>
                  </div>
                </div>

                {/* Negative points */}
                <div className="space-y-2 sm:space-y-3">
                  {[
                    'Une seule vitrine sur les plateformes',
                    'Visibilité limitée face à la concurrence',
                    "Chiffre d'affaires stagnant",
                    'Pas de stratégie marketing optimisée',
                  ].map((text, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 sm:w-7 sm:h-7 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-close-line text-base sm:text-lg text-red-400"></i>
                      </div>
                      <span className="text-gray-300 text-xs sm:text-base">{text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* APRÈS */}
              <div className="relative bg-[#FF007F]/5 backdrop-blur-sm rounded-3xl border-2 border-[#FF007F]/50 p-6 sm:p-10 overflow-hidden shadow-lg shadow-[#FF007F]/10">
                <div className="absolute top-0 left-0 bg-[#FF007F] text-white px-4 sm:px-6 py-2 rounded-br-2xl font-bold text-xs sm:text-sm tracking-wider">
                  APRÈS
                </div>
                <div className="mt-8 mb-4 sm:mb-6 text-center">
                  <h3 className="text-xl sm:text-3xl font-bold text-white mb-2">
                    Le restaurant{' '}
                    <span className="text-[#FF007F] font-extrabold">avec</span>{' '}
                    BlockFood
                  </h3>
                </div>

                {/* Desktop diagram */}
                <div className="hidden sm:flex items-center justify-between gap-3 mb-8">
                  {/* Restaurant */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#FF007F]/20 rounded-2xl flex items-center justify-center mb-2 ring-2 ring-[#FF007F]/40">
                      <i className="ri-store-2-line text-3xl sm:text-4xl text-[#FF007F]"></i>
                    </div>
                    <span className="text-xs sm:text-sm text-white font-semibold text-center">
                      Restaurant<br />
                      ABC
                    </span>
                  </div>

                  {/* BlockFood badge */}
                  <div className="flex-1 flex flex-col items-center">
                    <div className="bg-[#FF007F] text-white text-xs font-bold px-3 py-1 rounded-full mb-2 whitespace-nowrap">
                      BlockFood
                    </div>
                    <div className="flex items-center w-full">
                      <div className="flex-1 h-0.5 bg-[#FF007F]/60"></div>
                      <i className="ri-arrow-left-right-line text-xl text-[#FF007F] mx-1"></i>
                      <div className="flex-1 h-0.5 bg-[#FF007F]/60"></div>
                    </div>
                  </div>

                  {/* Multiple showcases */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="flex flex-col gap-1 mb-2">
                      <div className="flex gap-1">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#FF007F]/20 rounded-lg flex items-center justify-center ring-1 ring-[#FF007F]/30">
                          <i className="ri-riding-line text-sm sm:text-lg text-[#FF007F]"></i>
                        </div>
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#FF007F]/20 rounded-lg flex items-center justify-center ring-1 ring-[#FF007F]/30">
                          <i className="ri-e-bike-line text-sm sm:text-lg text-[#FF007F]"></i>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#FF007F]/20 rounded-lg flex items-center justify-center ring-1 ring-[#FF007F]/30">
                          <i className="ri-restaurant-line text-sm sm:text-lg text-[#FF007F]"></i>
                        </div>
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#FF007F]/20 rounded-lg flex items-center justify-center ring-1 ring-[#FF007F]/30">
                          <i className="ri-store-3-line text-sm sm:text-lg text-[#FF007F]"></i>
                        </div>
                      </div>
                    </div>
                    <span className="text-xs sm:text-sm text-[#FF007F] font-semibold text-center">
                      3 à 5<br />
                      vitrines
                    </span>
                  </div>

                  {/* Growth arrow */}
                  <div className="flex-1 flex flex-col items-center">
                    <div className="flex items-center w-full">
                      <div className="flex-1 h-0.5 bg-[#FF007F]/60"></div>
                      <i className="ri-arrow-right-line text-xl text-[#FF007F] mx-1"></i>
                      <div className="flex-1 h-0.5 bg-[#FF007F]/60"></div>
                    </div>
                    <span className="text-xs text-green-400 font-bold mt-1">x3</span>
                  </div>

                  {/* More customers */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-500/20 rounded-2xl flex items-center justify-center mb-2 ring-2 ring-green-500/40">
                      <i className="ri-team-line text-3xl sm:text-4xl text-green-400"></i>
                    </div>
                    <span className="text-xs sm:text-sm text-green-400 font-semibold">
                      3x plus<br />
                      de clients
                    </span>
                  </div>
                </div>

                {/* Mobile diagram */}
                <div className="flex sm:hidden items-center justify-center gap-2 mb-6">
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 bg-[#FF007F]/20 rounded-xl flex items-center justify-center mb-1 ring-2 ring-[#FF007F]/40">
                      <i className="ri-store-2-line text-2xl text-[#FF007F]"></i>
                    </div>
                    <span className="text-[10px] text-white font-semibold text-center">
                      Restaurant
                    </span>
                  </div>

                  <div className="flex flex-col items-center">
                    <i className="ri-arrow-right-line text-lg text-[#FF007F]"></i>
                    <span className="text-[9px] text-[#FF007F] font-bold">
                      BlockFood
                    </span>
                  </div>

                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 bg-[#FF007F]/20 rounded-xl flex items-center justify-center mb-1 ring-1 ring-[#FF007F]/30">
                      <i className="ri-apps-line text-2xl text-[#FF007F]"></i>
                    </div>
                    <span className="text-[10px] text-[#FF007F] font-semibold text-center">
                      3-5 vitrines
                    </span>
                  </div>

                  <i className="ri-arrow-right-line text-lg text-[#FF007F]"></i>

                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center mb-1 ring-2 ring-green-500/40">
                      <i className="ri-team-line text-2xl text-green-400"></i>
                    </div>
                    <span className="text-[10px] text-green-400 font-semibold text-center">
                      x3 clients
                    </span>
                  </div>
                </div>

                {/* Positive points */}
                <div className="space-y-2 sm:space-y-3">
                  {[
                    '3 à 5 vitrines sur chaque plateforme',
                    'Visibilité multipliée par 3 à 5',
                    "+20% à +40% de chiffre d'affaires",
                    "0€ d'investissement, BlockFood gère tout",
                  ].map((text, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 sm:w-7 sm:h-7 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-base sm:text-lg text-green-400"></i>
                      </div>
                      <span className="text-white text-xs sm:text-base font-medium">
                        {text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Résumé impact */}
            <div className="mt-8 sm:mt-12 text-center px-2">
              <div className="inline-flex items-center gap-2 sm:gap-4 bg-[#FF007F]/10 border-2 border-[#FF007F]/30 rounded-full px-4 sm:px-8 py-3 sm:py-4">
                <i className="ri-arrow-up-circle-fill text-2xl sm:text-3xl text-[#FF007F] flex-shrink-0"></i>
                <span className="text-sm sm:text-xl font-bold text-white">
                  Résultat : jusqu&apos;à{' '}
                  <span className="text-[#FF007F]">+40% de ventes</span> sans rien
                  changer en cuisine
                </span>
              </div>
            </div>
          </div>

          {/* Smartphone Showcase */}
          <div className="mb-12 sm:mb-20">
            <div className="flex justify-center items-end gap-3 sm:gap-8 overflow-hidden px-2 sm:px-4">
              {/* Phone 1 */}
              <div className="relative flex-shrink-0 transform hover:scale-105 transition-transform duration-300">
                <div className="w-28 sm:w-48 h-56 sm:h-96 bg-gray-900 rounded-[1.5rem] sm:rounded-[2rem] p-1.5 sm:p-2 shadow-2xl shadow-[#FF007F]/20 border-2 sm:border-4 border-gray-800">
                  <div className="w-full h-full bg-white rounded-[1.2rem] sm:rounded-[1.5rem] overflow-hidden relative">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-14 sm:w-20 h-4 sm:h-6 bg-gray-900 rounded-b-lg sm:rounded-b-xl"></div>
                    <img
                      src="https://readdy.ai/api/search-image?query=mobile%20app%20food%20delivery%20interface%20showing%20gourmet%20burger%20restaurant%20brand%20with%20appetizing%20burger%20photos%20menu%20items%20prices%20and%20order%20button%20modern%20UI%20design%20clean%20layout%20warm%20orange%20and%20red%20colors%20professional%20food%20photography&width=400&height=800&seq=phone-brand-001&orientation=portrait"
                      alt="Marque virtuelle Burger Gourmet"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <p className="text-center mt-2 sm:mt-4 text-xs sm:text-sm font-semibold text-[#FF007F]">
                  Burger Gourmet
                </p>
              </div>

              {/* Phone 2 - Center */}
              <div className="relative flex-shrink-0 transform scale-105 sm:scale-110 hover:scale-110 sm:hover:scale-115 transition-transform duration-300 z-10">
                <div className="w-32 sm:w-56 h-64 sm:h-[26rem] bg-gray-900 rounded-[1.8rem] sm:rounded-[2.5rem] p-1.5 sm:p-2 shadow-2xl shadow-[#FF007F]/40 border-2 sm:border-4 border-[#FF007F]/50">
                  <div className="w-full h-full bg-white rounded-[1.4rem] sm:rounded-[2rem] overflow-hidden relative">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 sm:w-24 h-5 sm:h-7 bg-gray-900 rounded-b-lg sm:rounded-b-xl"></div>
                    <img
                      src="https://readdy.ai/api/search-image?query=mobile%20app%20food%20delivery%20interface%20showing%20authentic%20asian%20cuisine%20restaurant%20brand%20with%20sushi%20and%20ramen%20dishes%20beautiful%20food%20photography%20menu%20layout%20modern%20minimalist%20design%20green%20and%20black%20colors%20professional%20UI&width=400&height=800&seq=phone-brand-002&orientation=portrait"
                      alt="Marque virtuelle Asian Fusion"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <p className="text-center mt-2 sm:mt-4 text-xs sm:text-sm font-semibold text-[#FF007F]">
                  Asian Fusion
                </p>
              </div>

              {/* Phone 3 */}
              <div className="relative flex-shrink-0 transform hover:scale-105 transition-transform duration-300">
                <div className="w-28 sm:w-48 h-56 sm:h-96 bg-gray-900 rounded-[1.5rem] sm:rounded-[2rem] p-1.5 sm:p-2 shadow-2xl shadow-[#FF007F]/20 border-2 sm:border-4 border-gray-800">
                  <div className="w-full h-full bg-white rounded-[1.2rem] sm:rounded-[1.5rem] overflow-hidden relative">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-14 sm:w-20 h-4 sm:h-6 bg-gray-900 rounded-b-lg sm:rounded-b-xl"></div>
                    <img
                      src="https://readdy.ai/api/search-image?query=mobile%20app%20food%20delivery%20interface%20showing%20italian%20pizza%20restaurant%20brand%20with%20delicious%20pizza%20photos%20menu%20items%20modern%20app%20design%20red%20and%20cream%20colors%20appetizing%20food%20photography%20clean%20UI%20layout&width=400&height=800&seq=phone-brand-003&orientation=portrait"
                      alt="Marque virtuelle Pizza Napoli"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <p className="text-center mt-2 sm:mt-4 text-xs sm:text-sm font-semibold text-[#FF007F]">
                  Pizza Napoli
                </p>
              </div>
            </div>
            <p className="text-center text-gray-400 mt-6 sm:mt-8 text-sm sm:text-lg">
              Exemples de marques virtuelles créées par BlockFood
            </p>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white/5 backdrop-blur-sm p-6 sm:p-8 rounded-2xl border border-white/10 hover:border-[#FF007F]/50 transition-all duration-300">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#FF007F] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-store-3-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">
                Marques virtuelles optimisées
              </h3>
              <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                Création de concepts de restaurants virtuels parfaitement adaptés
                aux tendances du marché.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm p-6 sm:p-8 rounded-2xl border border-white/10 hover:border-[#FF007F]/50 transition-all duration-300">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#FF007F] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-palette-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Branding &amp; visuels</h3>
              <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                Gestion complète de l&apos;identité visuelle et du branding pour
                attirer les clients.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm p-6 sm:p-8 rounded-2xl border border-white/10 hover:border-[#FF007F]/50 transition-all duration-300">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#FF007F] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-megaphone-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Marketing ciblé</h3>
              <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                Stratégies marketing personnalisées pour maximiser votre
                visibilité et vos ventes.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm p-6 sm:p-8 rounded-2xl border border-white/10 hover:border-[#FF007F]/50 transition-all duration-300">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#FF007F] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-apps-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Intégration plateformes</h3>
              <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                Présence sur Uber Eats, Deliveroo, Just Eat et toutes les
                plateformes majeures.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm p-6 sm:p-8 rounded-2xl border border-white/10 hover:border-[#FF007F]/50 transition-all duration-300">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#FF007F] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-money-euro-circle-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Aucun coût</h3>
              <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                0€ d&apos;investissement de votre part. Nous prenons tous les
                risques financiers.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm p-6 sm:p-8 rounded-2xl border border-white/10 hover:border-[#FF007F]/50 transition-all duration-300">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#FF007F] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-shield-check-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Aucun risque</h3>
              <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                Pas d&apos;engagement, pas de contrainte. Vous gardez le contrôle
                total de votre cuisine.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 sm:py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-black mb-4 sm:mb-6">
              Pourquoi choisir <span className="text-[#FF007F]">BlockFood</span> ?
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Des résultats concrets et mesurables pour votre restaurant
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center border-2 border-[#FF007F]/20">
              <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">📈</div>
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 sm:mb-4">
                +20% à +30% de CA
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                Augmentation moyenne du chiffre d&apos;affaires constatée chez nos
                partenaires.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center border-2 border-[#FF007F]/20">
              <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">✅</div>
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 sm:mb-4">
                0€ d&apos;investissement
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                Aucun frais à avancer. Nous investissons pour vous et partageons
                les résultats.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center border-2 border-[#FF007F]/20">
              <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">🚀</div>
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 sm:mb-4">
                Plus de visibilité
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                Multipliez votre présence sur toutes les plateformes de livraison.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center border-2 border-[#FF007F]/20">
              <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">🎯</div>
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 sm:mb-4">
                Gestion complète
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                BlockFood s&apos;occupe de tout : branding, marketing, optimisation
                et suivi.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center border-2 border-[#FF007F]/20">
              <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">🍔</div>
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 sm:mb-4">
                Ventes quotidiennes
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                De nouvelles commandes chaque jour grâce à vos marques virtuelles.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center border-2 border-[#FF007F]/20">
              <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">⚡</div>
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 sm:mb-4">
                Mise en place rapide
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                Vos marques virtuelles sont opérationnelles en quelques jours
                seulement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 sm:py-24 bg-[#FF007F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
              Ils ont testé, ils approuvent
            </h2>
            <p className="text-base sm:text-xl text-white/90 max-w-3xl mx-auto">
              Découvrez les témoignages de restaurateurs qui ont boosté leurs
              ventes avec BlockFood
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {/* Testimonial 1 */}
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl">
              <div className="flex items-center mb-4 sm:mb-6">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#FF007F] rounded-full flex items-center justify-center text-white text-xl sm:text-2xl font-bold mr-3 sm:mr-4 flex-shrink-0">
                  K
                </div>
                <div>
                  <h4 className="text-lg sm:text-xl font-bold text-black">Karim</h4>
                  <p className="text-gray-600 text-sm sm:text-base">Snack Royal</p>
                </div>
              </div>
              <div className="flex mb-3 sm:mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-xl sm:text-2xl text-[#FF007F]"></i>
                ))}
              </div>
              <p className="text-gray-700 text-sm sm:text-lg leading-relaxed italic">
                “Grâce à BlockFood, nous avons augmenté nos ventes de +37% en 2
                mois ! Je ne pensais pas que c&apos;était possible sans investir.
                L&apos;équipe gère tout et les résultats sont là.”
              </p>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl">
              <div className="flex items-center mb-4 sm:mb-6">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#FF007F] rounded-full flex items-center justify-center text-white text-xl sm:text-2xl font-bold mr-3 sm:mr-4 flex-shrink-0">
                  S
                </div>
                <div>
                  <h4 className="text-lg sm:text-xl font-bold text-black">Sofia</h4>
                  <p className="text-gray-600 text-sm sm:text-base">Pizza Time</p>
                </div>
              </div>
              <div className="flex mb-3 sm:mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-xl sm:text-2xl text-[#FF007F]"></i>
                ))}
              </div>
              <p className="text-gray-700 text-sm sm:text-lg leading-relaxed italic">
                “Je ne pensais pas que c&apos;était possible sans rien investir…
                BlockFood l&apos;a fait ! Nos marques virtuelles génèrent maintenant
                25% de notre chiffre d&apos;affaires total.”
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Form Section */}
      <section
        id="contact-form"
        className="py-12 sm:py-24 bg-gradient-to-b from-gray-50 to-white"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-black mb-4 sm:mb-6">
              Réservez votre rendez-vous{' '}
              <span className="text-[#FF007F]">gratuit</span> dès aujourd&apos;hui
            </h2>
            <p className="text-base sm:text-xl text-gray-600">
              Discutons de votre projet et découvrez comment augmenter vos ventes
              sans risque
            </p>
          </div>

          <div className="bg-white p-6 sm:p-12 rounded-3xl shadow-2xl border-2 border-[#FF007F]/20">
            <form
              id="ia-cool-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4 sm:space-y-6"
            >
              {/* Nom */}
              <div>
                <label
                  htmlFor="nom"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Votre nom complet *
                </label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#FF007F] focus:outline-none transition-colors text-sm sm:text-base"
                  placeholder="Jean Dupont"
                />
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Votre email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#FF007F] focus:outline-none transition-colors text-sm sm:text-base"
                  placeholder="jean.dupont@email.com"
                />
              </div>

              {/* Téléphone */}
              <div>
                <label
                  htmlFor="telephone"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Votre téléphone *
                </label>
                <input
                  type="tel"
                  id="telephone"
                  name="telephone"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#FF007F] focus:outline-none transition-colors text-sm sm:text-base"
                  placeholder="+33 6 12 34 56 78"
                />
              </div>

              {/* Restaurant */}
              <div>
                <label
                  htmlFor="restaurant"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Nom de votre restaurant *
                </label>
                <input
                  type="text"
                  id="restaurant"
                  name="restaurant"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#FF007F] focus:outline-none transition-colors text-sm sm:text-base"
                  placeholder="Le Bon Goût"
                />
              </div>

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full bg-[#FF007F] text-white px-8 py-4 sm:py-5 rounded-full text-base sm:text-lg font-bold hover:bg-[#E6006F] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting'
                  ? 'Envoi en cours...'
                  : 'Réserver mon rendez-vous gratuit'}
              </button>

              {formStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-500 text-green-700 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-center text-sm sm:text-base">
                  ✅ Merci ! Votre demande a été reçue par BlockFood. Nous vous
                  contacterons très bientôt.
                </div>
              )}

              {formStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-500 text-red-700 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-center text-sm sm:text-base">
                  ❌ Une erreur s&apos;est produite. Veuillez réessayer.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-24 bg-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
              Questions fréquentes
            </h2>
            <p className="text-base sm:text-xl text-gray-300">
              Toutes les réponses à vos questions
            </p>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {[
              {
                id: 1,
                question: 'Est-ce que ça me coûte quelque chose ?',
                answer:
                  "Non, c’est 100% gratuit. Vous n’avez aucun frais à avancer. BlockFood prend en charge tous les coûts de création, de branding et de marketing. Nous partageons ensuite les bénéfices générés par les marques virtuelles.",
              },
              {
                id: 2,
                question: 'Dois-je changer mon menu ?',
                answer:
                  "Non, absolument pas. Nous créons des marques virtuelles autour de votre carte existante. Vous continuez à préparer les mêmes plats avec les mêmes ingrédients. Nous optimisons simplement la présentation et le marketing pour attirer de nouveaux clients.",
              },
              {
                id: 3,
                question: 'Et si ça ne marche pas ?',
                answer:
                  "Aucun risque, aucun engagement. Si les résultats ne sont pas au rendez‑vous, vous pouvez arrêter à tout moment sans aucune pénalité. Mais nos statistiques montrent que 95 % de nos partenaires constatent une augmentation significative de leurs ventes dès les premières semaines.",
              },
              {
                id: 4,
                question: 'Combien de temps faut‑il pour voir des résultats ?',
                answer:
                  "Les premières commandes arrivent généralement dans les 7 à 10 jours suivant le lancement de vos marques virtuelles. L’augmentation significative du chiffre d’affaires se constate généralement après 4 à 6 semaines d’optimisation continue.",
              },
              {
                id: 5,
                question: 'Combien de marques virtuelles allez‑vous créer ?',
                answer:
                  "Cela dépend de votre menu et de votre capacité de production. En moyenne, nous créons entre 2 et 5 marques virtuelles par restaurant. Chaque marque cible un segment de clientèle différent pour maximiser votre portée et vos ventes.",
              },
            ].map((faq) => (
              <div
                key={faq.id}
                className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden"
              >
                <button
                  onClick={() =>
                    setOpenFaq(openFaq === faq.id ? null : faq.id)
                  }
                  className="w-full px-5 sm:px-8 py-4 sm:py-6 flex justify-between items-center hover:bg-white/5 transition-colors cursor-pointer"
                >
                  <h3 className="text-base sm:text-xl font-bold text-left pr-4">
                    {faq.question}
                  </h3>
                  <i
                    className={`ri-arrow-${
                      openFaq === faq.id ? 'up' : 'down'
                    }-s-line text-xl sm:text-2xl text-[#FF007F] flex-shrink-0`}
                  ></i>
                </button>
                {openFaq === faq.id && (
                  <div className="px-5 sm:px-8 pb-4 sm:pb-6">
                    <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#FF007F] text-white py-10 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12 mb-8 sm:mb-12">
            {/* Brand */}
            <div>
              <div className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">
                BlockFood
              </div>
              <p className="text-white/90 leading-relaxed text-sm sm:text-base">
                Augmentez vos ventes de +20% à +30% sans investir 1€. Nous créons
                des marques virtuelles qui génèrent des revenus additionnels pour
                votre restaurant.
              </p>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">
                Contact
              </h4>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center">
                  <i className="ri-mail-line text-xl sm:text-2xl mr-3"></i>
                  <a
                    href="mailto:contact@blockfood.fr"
                    className="hover:text-white/80 transition-colors cursor-pointer text-sm sm:text-base"
                  >
                    contact@blockfood.fr
                  </a>
                </div>
                <div className="flex items-center">
                  <i className="ri-phone-line text-xl sm:text-2xl mr-3"></i>
                  <a
                    href="tel:+33756820530"
                    className="hover:text-white/80 transition-colors cursor-pointer text-sm sm:text-base"
                  >
                    07 56 82 05 30
                  </a>
                </div>
              </div>
            </div>

            {/* Social */}
            <div>
              <h4 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">
                Suivez‑nous
              </h4>
              <div className="flex space-x-3 sm:space-x-4">
                <a href="#" rel="nofollow" className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl sm:text-2xl"></i>
                </a>
                <a href="#" rel="nofollow" className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl sm:text-2xl"></i>
                </a>
                <a href="#" rel="nofollow" className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-xl sm:text-2xl"></i>
                </a>
                <a href="#" rel="nofollow" className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line text-xl sm:text-2xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-6 sm:pt-8 text-center">
            <p className="text-white/80 text-sm sm:text-base">
              © 2025 BlockFood. Tous droits réservés. |{' '}
              <a
                href="https://readdy.ai/?ref=logo"
                rel="nofollow"
                className="hover:text-white transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
